import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatGraphic } from './chat-graphic';
import { GrowGraphic } from './grow-graphic';
import { LaunchGraphic } from './launch-graphic';

/**
 * "How it works" — option 1a from the Claude Design project.
 *
 * Three cards, one active at a time. The active card fills yellow, lifts into
 * its sticker shadow, replays its graphic and runs a timer bar; when the bar
 * completes, focus moves to the next card.
 *
 * Departures from the design doc, all for accessibility:
 * - Cards are <button>s, not click-handled divs, so they are reachable and
 *   operable by keyboard.
 * - Cycling pauses on focus as well as hover. Content that advances itself
 *   while someone is reading it is the problem; a mouse is not the only way to
 *   be reading it.
 * - Auto-advance never starts under prefers-reduced-motion. Clicking still
 *   works, so the content stays fully reachable without any motion.
 */

const CYCLE_MS = 6000;

const steps = [
  {
    id: 'launch',
    label: 'Step 01',
    title: 'Launch',
    body: 'We build you a modern Shopify store that matches your brand and has a great user experience.',
    Graphic: LaunchGraphic,
  },
  {
    id: 'manage',
    label: 'Step 02',
    title: 'Manage',
    body: 'Delegate the maintenance work: adding and updating products, conversion optimization, SEO, security, platform updates, and more, so you can stay focused on running your business.',
    Graphic: ChatGraphic,
  },
  {
    id: 'grow',
    label: 'Step 03',
    title: 'Grow',
    body: "We actively learn, optimize, and repeat as your website turns into a digital storefront that's open 24/7, making sales on autopilot.",
    Graphic: GrowGraphic,
  },
];

export function HowItWorks() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [motionOk, setMotionOk] = useState(false);
  /**
   * One counter per step, bumped only when that step becomes active. Its
   * graphic is keyed on it, so activating a card remounts just that graphic and
   * replays it. A single shared counter looked right but quietly replayed the
   * other two as well, leaving inactive cards animating in the background.
   */
  const [runs, setRuns] = useState<number[]>(() => steps.map(() => 0));
  // React 19's useRef requires an explicit initial value.
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setMotionOk(!query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!motionOk || paused) return;
    timer.current = setTimeout(() => {
      const next = (active + 1) % steps.length;
      setActive(next);
      setRuns((prev) => prev.map((n, i) => (i === next ? n + 1 : n)));
    }, CYCLE_MS);
    return () => clearTimeout(timer.current);
  }, [active, paused, motionOk, runs]);

  const select = useCallback((index: number) => {
    setActive(index);
    setRuns((prev) => prev.map((n, i) => (i === index ? n + 1 : n)));
  }, []);

  return (
    <div
      data-hiw
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {steps.map((step, i) => {
          const isActive = i === active;
          const { Graphic } = step;
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => select(i)}
              aria-pressed={isActive}
              className="group flex flex-col overflow-hidden rounded-[20px] border-2 border-bw-ink bg-white text-left shadow-sticker transition-[transform,background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-bw-ink focus-visible:ring-offset-2 aria-pressed:-translate-x-0.5 aria-pressed:-translate-y-0.5 aria-pressed:bg-bw-yellow aria-pressed:shadow-sticker-lg"
              style={{ transitionTimingFunction: 'var(--ease-bounce)' }}
            >
              <span className="block h-[290px] border-b-2 border-bw-ink bg-white">
                {/* Keyed on this step's own counter, so only the card being
                    activated remounts and replays. */}
                <Graphic key={runs[i]} />
              </span>

              <span className="flex flex-1 flex-col gap-2.5 px-6 pb-5 pt-[22px]">
                {/* ink-700 rather than ink-500: the card turns yellow when selected,
                    and ink-500 on that yellow is 3.76:1 -- under the 4.5:1 needed
                    at 12px. ink-700 clears it on both surfaces. */}
                <span className="bw-eyebrow text-ink-700">{step.label}</span>
                <span className="bw-display text-[24px] leading-[1.05] text-bw-ink">
                  {step.title}
                </span>
                <span className="text-[15px] leading-[1.6] text-ink-700">
                  {step.body}
                </span>
              </span>

              {/* Timer bar. Decorative: the button's pressed state already
                  conveys which card is active. */}
              <span
                aria-hidden="true"
                className={`block h-[5px] ${isActive ? 'bg-black/10' : 'bg-transparent'}`}
              >
                {isActive && motionOk ? (
                  <span
                    key={runs[i]}
                    className="block h-full origin-left bg-bw-ink"
                    style={{
                      animation: `hiw-prog ${CYCLE_MS}ms linear both`,
                      animationPlayState: paused ? 'paused' : 'running',
                    }}
                  />
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
